/**
 * N8N Workflows Admin View
 *
 * Manage n8n workflow registrations: list, create, edit, delete, toggle active.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type N8nWorkflowEntry = {
  id: string;
  projectId: string;
  projectName?: string;
  name: string;
  n8nWorkflowId: string;
  n8nBaseUrl: string;
  n8nApiKey?: string;
  description?: string;
  triggerType?: string;
  inboundWebhookPath?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type N8nAdminProps = {
  workflows: N8nWorkflowEntry[];
  loading: boolean;
  error: string | null;
  createMode: boolean;
  editingId: string | null;
  form: Partial<N8nWorkflowEntry> | null;
  filterProjectId: string | null;
  projects: Array<{ id: string; name: string; slug?: string }>;
  onRefresh: () => void;
  onCreate: () => void;
  onSave: () => void;
  onEdit: (wf: N8nWorkflowEntry) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, active: boolean) => void;
  onFormChange: (field: string, value: unknown) => void;
  onFilterChange: (projectId: string | null) => void;
};

// --------------- Render ---------------

export function renderN8nAdmin(props: N8nAdminProps) {
  const isEditing = props.createMode || props.editingId != null;

  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">
      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
        <div style="display:flex;align-items:center;gap:12px;">
          <label class="field" style="margin:0;min-width:200px;">
            <select
              .value=${props.filterProjectId ?? ""}
              @change=${(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                props.onFilterChange(v || null);
              }}
            >
              <option value="">All Projects</option>
              ${props.projects.map((p) => html`<option value=${p.id}>${p.name}</option>`)}
            </select>
          </label>
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <button class="btn primary" @click=${props.onCreate} ?disabled=${isEditing}>
            + New Workflow
          </button>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      <!-- Create / Edit Form -->
      ${isEditing && props.form ? renderForm(props) : nothing}

      <!-- Workflows Table -->
      ${
        props.workflows.length === 0 && !props.loading
          ? html`
              <div class="callout info">
                No n8n workflow registrations found. Click "+ New Workflow" to connect your first n8n workflow.
              </div>
            `
          : nothing
      }

      ${
        props.workflows.length > 0
          ? html`
            <div class="card">
              <div class="table">
                <div class="table-head">
                  <span style="flex:2">Name</span>
                  <span style="flex:1.5">Project</span>
                  <span style="flex:1.5">Workflow ID</span>
                  <span style="flex:1">Trigger</span>
                  <span style="flex:0.8">Status</span>
                  <span style="flex:1.5;text-align:right">Actions</span>
                </div>
                ${props.workflows.map(
                  (wf) => html`
                  <div class="table-row">
                    <span style="flex:2">
                      <strong>${wf.name}</strong>
                      ${wf.description ? html`<br/><span class="muted" style="font-size:0.85em">${wf.description}</span>` : nothing}
                    </span>
                    <span style="flex:1.5" class="muted">${wf.projectName ?? wf.projectId.slice(0, 8)}</span>
                    <span style="flex:1.5" class="mono" style="font-size:0.85em">${wf.n8nWorkflowId}</span>
                    <span style="flex:1">
                      <span class="chip">${wf.triggerType ?? "manual"}</span>
                    </span>
                    <span style="flex:0.8">
                      <span class="chip ${wf.isActive ? "chip-ok" : "chip-warn"}">
                        ${wf.isActive ? "Active" : "Paused"}
                      </span>
                    </span>
                    <span style="flex:1.5;display:flex;gap:6px;justify-content:flex-end">
                      <button
                        class="btn btn--sm"
                        @click=${() => props.onToggle(wf.id, !wf.isActive)}
                      >
                        ${wf.isActive ? "Pause" : "Activate"}
                      </button>
                      <button class="btn btn--sm" @click=${() => props.onEdit(wf)}>Edit</button>
                      <button class="btn btn--sm danger" @click=${() => props.onDelete(wf.id)}>Delete</button>
                    </span>
                  </div>
                `,
                )}
              </div>
            </div>
          `
          : nothing
      }

      <!-- Webhook Info -->
      ${
        props.workflows.filter((wf) => wf.inboundWebhookPath).length > 0
          ? html`
            <div class="card">
              <div class="card-title">Inbound Webhook URLs</div>
              <div class="card-sub">These URLs receive callbacks from n8n workflows.</div>
              <div style="margin-top:12px;">
                ${props.workflows
                  .filter((wf) => wf.inboundWebhookPath)
                  .map(
                    (wf) => html`
                      <div style="display:flex;align-items:center;gap:8px;padding:6px 0;">
                        <strong style="min-width:120px">${wf.name}:</strong>
                        <code class="mono" style="font-size:0.85em;color:var(--accent)">/api/n8n/${wf.inboundWebhookPath}</code>
                      </div>
                    `,
                  )}
              </div>
            </div>
          `
          : nothing
      }
    </div>
  `;
}

// --------------- Form ---------------

function renderForm(props: N8nAdminProps) {
  const form = props.form!;
  const isEdit = props.editingId != null;
  return html`
    <div class="card">
      <div class="card-title">${isEdit ? "Edit Workflow" : "Register New N8N Workflow"}</div>
      <div class="form-grid" style="margin-top:12px;">
        ${
          !isEdit
            ? html`
              <label class="field">
                <span>Project</span>
                <select
                  .value=${String(form.projectId ?? "")}
                  @change=${(e: Event) =>
                    props.onFormChange("projectId", (e.target as HTMLSelectElement).value)}
                >
                  <option value="">Select project...</option>
                  ${props.projects.map(
                    (p) =>
                      html`<option value=${p.id} ?selected=${p.id === form.projectId}>${p.name}</option>`,
                  )}
                </select>
              </label>
            `
            : nothing
        }
        <label class="field">
          <span>Name</span>
          <input
            .value=${String(form.name ?? "")}
            @input=${(e: Event) => props.onFormChange("name", (e.target as HTMLInputElement).value)}
            placeholder="My Workflow"
          />
        </label>
        <label class="field">
          <span>N8N Workflow ID</span>
          <input
            .value=${String(form.n8nWorkflowId ?? "")}
            @input=${(e: Event) =>
              props.onFormChange("n8nWorkflowId", (e.target as HTMLInputElement).value)}
            placeholder="e.g. 12345 or workflow-slug"
          />
        </label>
        <label class="field">
          <span>N8N Base URL</span>
          <input
            .value=${String(form.n8nBaseUrl ?? "")}
            @input=${(e: Event) =>
              props.onFormChange("n8nBaseUrl", (e.target as HTMLInputElement).value)}
            placeholder="http://localhost:5678"
          />
        </label>
        <label class="field">
          <span>N8N API Key</span>
          <input
            type="password"
            .value=${String(form.n8nApiKey ?? "")}
            @input=${(e: Event) =>
              props.onFormChange("n8nApiKey", (e.target as HTMLInputElement).value)}
            placeholder="Optional API key"
          />
        </label>
        <label class="field">
          <span>Trigger Type</span>
          <select
            .value=${String(form.triggerType ?? "manual")}
            @change=${(e: Event) =>
              props.onFormChange("triggerType", (e.target as HTMLSelectElement).value)}
          >
            <option value="manual">Manual</option>
            <option value="webhook">Webhook</option>
            <option value="schedule">Schedule</option>
          </select>
        </label>
        <label class="field">
          <span>Inbound Webhook Path</span>
          <input
            .value=${String(form.inboundWebhookPath ?? "")}
            @input=${(e: Event) =>
              props.onFormChange("inboundWebhookPath", (e.target as HTMLInputElement).value)}
            placeholder="e.g. my-workflow (optional)"
          />
        </label>
        <label class="field full">
          <span>Description</span>
          <input
            .value=${String(form.description ?? "")}
            @input=${(e: Event) =>
              props.onFormChange("description", (e.target as HTMLInputElement).value)}
            placeholder="Brief description of this workflow"
          />
        </label>
      </div>
      <div style="display:flex;gap:8px;margin-top:16px;">
        <button class="btn primary" @click=${props.onSave} ?disabled=${props.loading}>
          ${isEdit ? "Update" : "Create"}
        </button>
        <button class="btn" @click=${props.onCancel}>Cancel</button>
      </div>
    </div>
  `;
}
